import * as React from "react"
import { cva } from "class-variance-authority"
import { CheckIcon, XCircle, ChevronDown, XIcon, Search } from "lucide-react"
import { cn } from "../../Lib/Cn"
import { Separator } from "./separator"
import { Button } from "./button"
import { Badge } from "./badge"
import { Popover, PopoverContent, PopoverTrigger } from "./popover"
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@radix-ui/react-hover-card"
import { useSelector } from "react-redux"
import { checkIfInqOnlyMode } from "../../Store/Reducers"

/**
 * Variants for the multi-select component to handle different styles.
 */
const multiSelectVariants = cva("m-1 transition ease-in-out delay-150 hover:-translate-y-1 duration-200", {
  variants: {
    variant: {
      default: "border-foreground/10 text-foreground bg-card hover:bg-card/80",
      secondary: "border-foreground/10 bg-secondary text-secondary-foreground hover:bg-secondary/80",
      destructive: "border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80",
      inverted: "inverted",
    },
  },
  defaultVariants: {
    variant: "default",
  },
})

// Helper to extract IDs from selected option objects
const getIDArr = (selectedObjArr) => {
  return selectedObjArr.map((option) => {
    return option.value
  })
}

export const MultiSelect = React.forwardRef(
  (
    {
      options,
      onValueChange,
      variant,
      defaultValue = [],
      placeholder = "Select options",
      emptyMessage = "No result found",
      animation = 0,
      maxCount = 3,
      modalPopover = false,
      asChild = false,
      className,
      isSelectAll = true,
      isSearch = true,
      closeIcon = true,
      outsideLabel = false,
      selectWidth = "100%",
      searchPlaceholder = "Search...",
      ...props
    },
    ref,
  ) => {
    const [selectedValues, setSelectedValues] = React.useState([])
    const [isPopoverOpen, setIsPopoverOpen] = React.useState(false)
    const [isAnimating, setIsAnimating] = React.useState(false)
    const [searchTerm, setSearchTerm] = React.useState("")
    const [isMouseOver, setIsMouseOver] = React.useState(false)
    const [threadTypeWiseCount, setThreadTypeWiseCount] = React.useState({})
    const isInqOnlyMode = useSelector(checkIfInqOnlyMode);
    const triggerRef = React.useRef(null)
    const scrollContainerRef = React.useRef(null)

    // Sync internal state with defaultValue changes
    React.useEffect(() => {
      const safeDefaultValue = Array.isArray(defaultValue) ? defaultValue : []
      
      const initialSelectedOptions = safeDefaultValue
        .map((value) => options.find((option) => option.value === value))
        .filter(Boolean)

      setSelectedValues(initialSelectedOptions)
    }, [defaultValue, options])

    React.useEffect(() => {
        const typeWiseCnt = selectedValues.reduce((acc, item) => {
          if (item?.typeID) {
            acc[item.typeID] = (acc[item.typeID] || 0) + 1
          }
          return acc
        }, {})
        setThreadTypeWiseCount(typeWiseCnt)
    }, [selectedValues]) 

    React.useEffect(() => {
      const handleWheel = (e) => {
        if (isPopoverOpen && scrollContainerRef.current) {
          const rect = scrollContainerRef.current.getBoundingClientRect()
          const isOverDropdown =
            e.clientX >= rect.left && e.clientX <= rect.right && e.clientY >= rect.top && e.clientY <= rect.bottom

          if (isOverDropdown) {
            e.preventDefault()
            e.stopPropagation()
            scrollContainerRef.current.scrollTop += e.deltaY
          }
        }
      }
      if (isPopoverOpen) {
        document.addEventListener("wheel", handleWheel, {
          passive: false,
          capture: true,
        })

        return () => {
          document.removeEventListener("wheel", handleWheel, { capture: true })
        }
      }
    }, [isPopoverOpen])

    const handleInputKeyDown = (event) => {
      if (event.key === "Enter") {
        setIsPopoverOpen(true)
      } else if (event.key === "Backspace" && !event.currentTarget.value) {
        const newSelectedValues = [...selectedValues]
        newSelectedValues.pop()
        setSelectedValues(newSelectedValues)
        onValueChange(getIDArr(newSelectedValues))
      }
    }

    // Enhanced filtering logic like CustomSearchableDropdown
    const filteredOptions = React.useMemo(() => {
      if (!searchTerm) return options
      
      const searchLower = searchTerm.toLowerCase()
      
      return options.filter((option) => {
        // Check label
        const label = option.label?.toLowerCase() || ''
        if (label.includes(searchLower)) return true
        
        // Check value (safely convert to string)
        const value = String(option.value || '').toLowerCase()
        if (value.includes(searchLower)) return true
        
        // Check product if exists
        const product = option.product?.toLowerCase() || ''
        if (product.includes(searchLower)) return true
        
        // Check threadType if exists
        const threadType = option.threadType?.toLowerCase() || ''
        if (threadType.includes(searchLower)) return true
        
        return false
      })
    }, [options, searchTerm])

    const toggleOption = (optionToToggle) => {
      const isSelected = selectedValues.some((selectedOption) => selectedOption.value === optionToToggle.value)

      const newSelectedValues = isSelected
        ? selectedValues.filter((selectedOption) => selectedOption.value !== optionToToggle.value)
        : [...selectedValues, optionToToggle]

      setSelectedValues(newSelectedValues)
      onValueChange(getIDArr(newSelectedValues))
    }

    const handleClear = () => {
      setSelectedValues([])
      onValueChange([])
    }

    const handleTogglePopover = () => {
      setIsPopoverOpen((prev) => !prev)
    }

    const clearExtraOptions = () => {
      const newSelectedValues = selectedValues.slice(0, maxCount)
      setSelectedValues(newSelectedValues)
      onValueChange(getIDArr(newSelectedValues))
    }

    const toggleAll = () => {
      if (selectedValues.length === options.length) {
        handleClear()
      } else {
        setSelectedValues(options)
        onValueChange(getIDArr(options))
      }
    }

    const mergeRefs = (...refs) => {
      return (node) => {
        refs.forEach((ref) => {
          if (typeof ref === "function") {
            ref(node)
          } else if (ref != null) {
            ref.current = node
          }
        })
      }
    }

    const getFormattedLabel = (optionValue) => {
      if (optionValue?.typeID && optionValue?.sinceDT && optionValue?.product) {
        if (threadTypeWiseCount[optionValue.typeID] === 1) {
          return `${!isInqOnlyMode ? optionValue.threadType + ' - ' : ''}${optionValue.product}`
        } else {
          return `${!isInqOnlyMode ? optionValue.threadType + ' - ' : ''}${optionValue.product} (${optionValue.sinceDT})`
        }
      }
      return optionValue?.label || ""
    }

    // Render badge content (reusable component)
    const renderBadgeContent = (option, showXIcon = true) => {
      const IconComponent = option?.icon
      
      const handleRemove = (event) => {
        event.preventDefault()
        event.stopPropagation()
        toggleOption(option)
      }
      
      return (
        <>
          {IconComponent && (
            <IconComponent className={`h-4 w-4 mr-2 ${option.color || ''}`} />
          )}
          <span className="truncate max-w-full">{getFormattedLabel(option)}</span>
          {showXIcon && (
            <XIcon
              className="ml-2 h-4 w-4 cursor-pointer"
              onClick={handleRemove}
              onMouseDown={handleRemove}
            />
          )}
        </>
      )
    }

    // Render selected badges
    const renderSelectedBadges = (isOutside = false) => {
      return (
        <>
          {selectedValues.slice(0, maxCount).map((option) => {
            const badgeContent = (
              <Badge
                className={cn(
                  isAnimating ? "animate-bounce" : "",
                  multiSelectVariants({ variant }),
                  "flex items-center max-w-full sahi-badge text-sahi-blue h-7"
                )}
                style={{ animationDuration: `${animation}s` }}
              >
                {renderBadgeContent(option)}
              </Badge>
            )

            if (option?.targetDT || option?.sinceDT) {
              return (
                <HoverCard key={option.value}>
                  <HoverCardTrigger asChild>
                    <div className="cursor-pointer inline-flex">
                      {badgeContent}
                    </div>
                  </HoverCardTrigger>

                  <HoverCardContent className="bg-white rounded-md shadow-2xl p-3 max-w-[250px] text-left" side="top">
                    <h4 className="text-sm font-bold mb-2">{option.threadType}</h4>
                    <div>
                      {option.product && (
                        <div>
                          <span className="text-muted-foreground">Product: </span>
                          <span>{option.product}</span>
                        </div>
                      )}
                      {option?.targetDT && (
                        <div>
                          <span className="text-muted-foreground">Due on: </span>
                          <span>{option.targetDT}</span>
                        </div>
                      )}
                      {option?.sinceDT && (
                        <div>
                          <span className="text-muted-foreground">Since: </span>
                          <span>{option.sinceDT}</span>
                        </div>
                      )}
                    </div>
                  </HoverCardContent>
                </HoverCard>
              )
            } else {
              return (
                <div key={option.value} className="inline-flex">
                  {badgeContent}
                </div>
              )
            }
          })}
          {selectedValues.length > maxCount && (
            <Badge
              className={cn(
                "bg-transparent text-foreground border-foreground/1 hover:bg-transparent h-7 min-w-0 max-w-full shrink",
                isAnimating ? "animate-bounce" : "",
                multiSelectVariants({ variant })
              )}
              style={{ animationDuration: `${animation}s` }}
            >
              <span className="truncate">{`+ ${selectedValues.length - maxCount} more`}</span>
              <XCircle
                className="ml-2 h-4 w-4 cursor-pointer"
                onClick={(event) => {
                  event.stopPropagation()
                  clearExtraOptions()
                }}
              />
            </Badge>
          )}
        </>
      )
    }

    return (
      <div className="w-full">
        <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen} modal={modalPopover}>
          <PopoverTrigger asChild>
            <Button
              ref={mergeRefs(ref, triggerRef)}
              {...props}
              onClick={handleTogglePopover}
              style={{ width: selectWidth }}
              className={cn(
                "flex sahi-input placeholder:text-muted-foreground min-h-11 h-auto items-center justify-between [&_svg]:pointer-events-auto p-1 overflow-x-auto",
                className,
              )}
            >
              {outsideLabel ? (
                <div className="flex items-center justify-between w-full mx-auto">
                  <span className="text-sm text-muted-foreground px-2 font-normal">
                    {selectedValues.length > 0 
                      ? `${selectedValues.length} ${props.label} selected` 
                      : placeholder
                    }
                  </span>
                  <ChevronDown className="h-4 cursor-pointer text-muted-foreground mx-2" />
                </div>
              ) : (
                selectedValues.length > 0 ? (
                  <div className="flex flex-wrap w-full gap-1 items-start min-w-0">
                    <div className="flex flex-wrap items-start gap-1 min-w-0 flex-1">
                      {renderSelectedBadges()}
                    </div>
                    <div className="flex flex-col items-start justify-start gap-2 mr-2 mt-2">
                      <div className="flex items-start gap-1">
                        {closeIcon && (
                          <XIcon
                            className="h-4 cursor-pointer text-muted-foreground"
                            onClick={(event) => {
                              event.stopPropagation()
                              handleClear()
                            }}
                          />
                        )}
                        <Separator orientation="vertical" className="h-6" />
                        <ChevronDown className="h-6 cursor-pointer text-muted-foreground" />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between w-full mx-auto">
                    <span className="text-sm text-muted-foreground px-2 font-normal">{placeholder}</span>
                    <ChevronDown className="h-4 cursor-pointer text-muted-foreground mx-2" />
                  </div>
                )
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent
            style={{
              width: triggerRef.current ? `${triggerRef.current.offsetWidth}px` : "auto",
            }}
            className="p-0"
            align="start"
            onEscapeKeyDown={() => setIsPopoverOpen(false)}
            onMouseEnter={() => setIsMouseOver(true)}
            onMouseLeave={() => setIsMouseOver(false)}
          >
            <div className="bg-primary-foreground h-full flex flex-col">
              {isSearch && (
                <div className="flex items-center border-b px-3">
                  <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
                  <input
                    type="text"
                    placeholder={searchPlaceholder}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={handleInputKeyDown}
                    autoFocus={isPopoverOpen}
                    className="flex h-11 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
                  />
                </div>
              )}

              <div
                ref={scrollContainerRef}
                className="flex-1 max-h-[300px] overflow-y-auto overflow-x-auto scroll-container"
              >
                <div className="p-2">
                  <div className="w-full">
                    {filteredOptions.length === 0 ? (
                      <div className="py-6 text-center text-sm text-muted-foreground">{emptyMessage}</div>
                    ) : (
                      <div className="w-full">
                        {isSelectAll && (
                          <div
                            onClick={toggleAll}
                            className="relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50"
                          >
                            <div
                              className={cn(
                                "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-sahi-primary-color",
                                selectedValues.length === options.length
                                  ? "bg-sahi-primary-color text-primary-foreground border-sahi-primary-color text-white"
                                  : "opacity-50 [&_svg]:invisible",
                              )}
                            >
                              <CheckIcon className="h-4 w-4" />
                            </div>
                            <span>Select All</span>
                          </div>
                        )}
                        {filteredOptions.map((option) => {
                          const isSelected = selectedValues.some(
                            (selectedOption) => selectedOption.value === option.value,
                          )
                          const IconComponent = option.icon
                          return (
                            <div
                              key={option.value}
                              onClick={() => toggleOption(option)}
                              className="relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50"
                            >
                              <div
                                className={cn(
                                  "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-sahi-primary-color",
                                  isSelected
                                    ? "bg-sahi-primary-color text-primary-foreground text-white"
                                    : "opacity-50 [&_svg]:invisible",
                                )}
                              >
                                <CheckIcon className="h-4 w-4" />
                              </div>
                              {IconComponent && (
                                <IconComponent
                                  className={`mr-2 h-4 w-4 text-muted-foreground ${option.color || ''}`}
                                />
                              )}
                            <span>{ Object.keys(threadTypeWiseCount).length == 0 ? option.label : `${!isInqOnlyMode ? option.threadType + ' - ' : ''}${option.product} (${option.sinceDT})`}</span>
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex-shrink-0 bg-primary-foreground border-t">
                <div className="border-t border-border"></div>
                <div className="p-1">
                  <div className="flex items-center justify-between">
                    {selectedValues.length > 0 && (
                      <>
                        <div
                          onClick={handleClear}
                          className="relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground flex-1 justify-center"
                        >
                          Clear
                        </div>
                        <Separator orientation="vertical" className="flex min-h-6 h-full" />
                      </>
                    )}
                    <div
                      onClick={() => setIsPopoverOpen(false)}
                      className="relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground flex-1 justify-center max-w-full"
                    >
                      Close
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </PopoverContent>
        </Popover>

        {/* OUTSIDE LABELS - Show below the select box when outsideLabel is true */}
        {outsideLabel && selectedValues.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {renderSelectedBadges()}
          </div>
        )}
      </div>
    )
  },
)

MultiSelect.displayName = "MultiSelect"